import {t} from '@lingui/macro';
import {isUndefined} from 'lodash';

import {CardModel, CardTypesEnum} from '../../models/api/cardModel';
import {ContactSourcesEnum, parseContactUid} from '../../models/api/contactModel';
import {Localizer} from '../../store/stateless/internationalizationSelectors';
import {computeIsGlobalNamespace, globalNamespace, isTeammateNamespace} from './namespaceHelpers';

/*
 * Helpers.
 */

export function isAutoGlobalCard(card: CardModel) {
  return card.type === CardTypesEnum.AUTO && card.namespace === globalNamespace;
}

export function isEnhancedCard(card: CardModel) {
  return card.type === CardTypesEnum.ENHANCED;
}

export function isEnhancedGlobalCard(card: CardModel) {
  return isEnhancedCard(card) && card.namespace === globalNamespace;
}

export function isEnhancedPrivateCard(card: CardModel) {
  return isEnhancedCard(card) && isTeammateNamespace(card.namespace);
}

export function computeMergeCardDetails(mainCard: CardModel, mergeCard: CardModel) {
  // If the merge card is a global card and the main card is private, merge into global card
  // It does not matter if the card is auto or company, we always merge private into global
  if (computeIsGlobalNamespace(mergeCard.namespace) && isTeammateNamespace(mainCard.namespace)) {
    return {mainCardId: mergeCard.id, mergeCardIds: [mainCard.id]};
  }

  return {mainCardId: mainCard.id, mergeCardIds: [mergeCard.id]};
}

export function shouldShowDuplicateCardInidicator(mainCard: CardModel, duplicateCard: CardModel): boolean {
  // If the duplicate card is not auto, we can always should the indicator
  if (duplicateCard.type !== CardTypesEnum.AUTO) {
    return true;
  }

  return (
    // Name of the cards are different
    mainCard.name !== duplicateCard.name ||
    // Duplicate card has notes attached
    duplicateCard.numberOfNotesViaNamespaces > 0 ||
    // Duplicate card has a description
    Boolean(duplicateCard.description) ||
    // Duplicate card has links
    duplicateCard.links.length > 0 ||
    // Duplicate card has more handles
    duplicateCard.contactUids.length > 1 ||
    // Duplicate card has custom fields
    (!isUndefined(duplicateCard.customFieldAttributes) && duplicateCard.customFieldAttributes.length > 0) ||
    // Duplicate card is a legacy card that is auto and has groups attached
    duplicateCard.groupIds.length > 0 ||
    // Duplicate card has an avatar
    Boolean(duplicateCard.avatarUrl)
  );
}

/**
 * From a list of passed in cards, look for the first card to display to the user.
 * Prioritized by importance.
 * @param cards List of cards to search through
 * @returns Card to display
 */
export function selectCardViaPriorityOrder(cards: ReadonlyArray<CardModel>) {
  // Prioritize company cards to show first
  const companyCard = cards.find(isEnhancedGlobalCard);
  if (companyCard) {
    return companyCard;
  }

  // Otherwise, show the enhanced private card
  const enhancedCard = cards.find(isEnhancedPrivateCard);
  if (enhancedCard) {
    return enhancedCard;
  }

  // Lastly, show an auto-created card
  const autoCreatedCard = cards.find(isAutoGlobalCard);
  if (autoCreatedCard) {
    return autoCreatedCard;
  }

  return cards[0];
}

export const getNameForCard = (card: CardModel) => {
  if (card.name) {
    return card.name;
  }

  const parsedContactUids = card.contactUids.map((contactUid) => parseContactUid(contactUid));
  if (parsedContactUids.length === 0) {
    return undefined;
  }

  // Try to get an email, if not default to the first contact uid in the list
  const emailContactUid = parsedContactUids.find((parsed) => parsed[0] === ContactSourcesEnum.EMAIL);
  if (emailContactUid) {
    return emailContactUid[1];
  }
  return parsedContactUids[0][1];
};

export function getCardHandlesByType(card: CardModel, handleType?: ContactSourcesEnum) {
  return card.contactUids
    .map((contactUid) => parseContactUid(contactUid))
    .filter((contactUid) => (handleType ? contactUid[0] === handleType : true))
    .map((contactUid) => contactUid[1]);
}

export function buildNoNameNoHandlesCardDisplayName(localize: Localizer) {
  return localize(t('contact-card-no-name-or-handles-display-name')`No name`);
}
